import React, { useState, useEffect, useContext } from 'react';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';
import { ChevronDown, Trash2 } from 'lucide-react';
import ConfirmationDialog from '../utils/ConfirmationDialog';

const ProcessBizDevStageDisplay = ({ individualId, onStageUpdate }) => {
  const API = useContext(ApiContext);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [processToDelete, setProcessToDelete] = useState(null);

  // Récupérer tous les processus BizDev pour cet individu (version optimisée)
  useEffect(() => {
    const fetchProcesses = async () => {
      setLoading(true);
      try {
        // Utiliser la route optimisée qui fait une seule requête
        const response = await API.get(`/api/process-bizdev/individual/${individualId}/processes`);
        setProcesses(response.data);
      } catch (error) {
        console.error('Error fetching BizDev processes:', error);
        toast.error('Erreur lors du chargement des processus BizDev');
      } finally {
        setLoading(false);
      }
    };

    if (individualId) {
      fetchProcesses();
    }
  }, [individualId, API]);

  // Déplacer vers une étape spécifique
  const moveToStage = async (process, newStageName) => {
    setLoading(true);
    try {
      await API.put(
        `/api/process-bizdev/${process.processId}/prospects/${individualId}/move`,
        { step: newStageName }
      );

      // Mettre à jour l'état local
      setProcesses(prevProcesses =>
        prevProcesses.map(p =>
          p.processId === process.processId
            ? { ...p, currentStep: newStageName }
            : p
        )
      );

      setOpenDropdown(null);
      toast.success(`Déplacé vers "${newStageName}"`);

      // Notifier le parent du changement
      if (onStageUpdate) {
        onStageUpdate();
      }
    } catch (error) {
      console.error('Error updating BizDev step:', error);
      toast.error('Erreur lors du déplacement');
    } finally {
      setLoading(false);
    }
  };

  // Ouvrir le dialogue de confirmation de suppression
  const handleRemoveClick = (process) => {
    setProcessToDelete(process);
    setShowDeleteDialog(true);
  };

  // Retirer le prospect du processus BizDev
  const confirmRemoveFromProcess = async () => {
    if (!processToDelete) return;

    setLoading(true);
    try {
      await API.delete(`/api/process-bizdev/${processToDelete.processId}/prospects/${individualId}`);

      // Retirer du state local
      setProcesses(prevProcesses =>
        prevProcesses.filter(p => p.processId !== processToDelete.processId)
      );

      toast.success(`Retiré du processus "${processToDelete.processName}"`);

      // Notifier le parent du changement
      if (onStageUpdate) {
        onStageUpdate();
      }

      setShowDeleteDialog(false);
      setProcessToDelete(null);
    } catch (error) {
      console.error('Error removing from BizDev process:', error);
      toast.error('Erreur lors de la suppression du processus');
    } finally {
      setLoading(false);
    }
  };

  // Fermer le dropdown si on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.process-bizdev-dropdown')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  return (
    <div className="mt-3 w-fit">
      <div className="space-y-2">
        {processes.map((process) => {
          const isDropdownOpen = openDropdown === process.processId;
          const activeSteps = process.steps?.filter(step => step.active).sort((a, b) => a.order - b.order) || [];

          return (
            <div
              key={process.processId}
              className="relative bg-indigo-50 border border-indigo-200 rounded process-bizdev-dropdown"
            >
              <div className="flex items-center p-2">
                {/* Icône ChevronDown */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDropdown(isDropdownOpen ? null : process.processId);
                  }}
                  className="p-1 hover:bg-indigo-100 rounded transition-colors mr-2"
                  title="Changer d'étape"
                  disabled={loading}
                >
                  <ChevronDown
                    size={16}
                    className={`text-indigo-700 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Nom du process et étape actuelle */}
                <div className="text-sm flex-1">
                  <span className="font-medium text-indigo-900">{process.processName}: </span>
                  <span className="text-indigo-700">{process.currentStep}</span>
                </div>

                {/* Bouton de suppression */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveClick(process);
                  }}
                  className="p-1 hover:bg-red-100 rounded transition-colors ml-2"
                  title="Retirer du processus"
                  disabled={loading}
                >
                  <Trash2 size={14} className="text-red-600" />
                </button>
              </div>

              {/* Dropdown des étapes disponibles */}
              {isDropdownOpen && activeSteps.length > 0 && (
                <div className="border-t border-indigo-200 bg-white rounded-b overflow-hidden">
                  <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                    {activeSteps.map((step) => {
                      const isCurrentStep = step.name === process.currentStep;
                      return (
                        <button
                          key={step._id}
                          onClick={() => {
                            if (!isCurrentStep) {
                              moveToStage(process, step.name);
                            }
                          }}
                          disabled={isCurrentStep || loading}
                          className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                            isCurrentStep
                              ? 'bg-indigo-50 text-indigo-700 font-medium cursor-default'
                              : 'hover:bg-gray-50 text-gray-700'
                          }`}
                        >
                          {step.name}
                          {isCurrentStep && (
                            <span className="ml-2 text-xs text-indigo-600">(actuel)</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Dialogue de confirmation de suppression */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setProcessToDelete(null);
        }}
        onConfirm={confirmRemoveFromProcess}
        title="Retirer du processus BizDev"
        message={`Êtes-vous sûr de vouloir retirer ce prospect du processus "${processToDelete?.processName}" ? Cette action est irréversible.`}
        confirmText="Retirer"
        cancelText="Annuler"
        isDangerous={true}
      />
    </div>
  );
};

export default ProcessBizDevStageDisplay;